import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { markFreshSignup } from '@/contexts/TourContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthCard } from './_AuthLayout';

export function SignUp() {
  const { signUp, signOut, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [codesOpen, setCodesOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Affiliate code is captured silently from the `?ref=` URL param only.
  // Promo code is still user-entered, but `?promo=` can prefill + auto-expand the section.
  useEffect(() => {
    const refQ = searchParams.get('ref') || '';
    const promoQ = searchParams.get('promo') || '';
    // Fallback: if no ?ref= on this URL, check localStorage where any
    // earlier landing on /?ref=CODE was captured by App.tsx.
    let effectiveRef = refQ;
    if (!effectiveRef) {
      try { effectiveRef = localStorage.getItem('telos:affiliate-ref') || ''; } catch { /* ignore */ }
    }
    if (effectiveRef) setAffiliateCode(effectiveRef.toUpperCase().trim());
    if (promoQ) setPromoCode(promoQ.toUpperCase().trim());
    if (promoQ) setCodesOpen(true);
  }, [searchParams]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setLoading(true);
    // Set the fresh-signup flag BEFORE awaiting signUp() — once the user
    // object lands in AuthContext, TourContext's effect fires mid-await
    // (React 18 concurrent mode flushes the state update before our
    // handler resumes). If the flag isn't already in localStorage at that
    // moment the effect bails and won't re-fire (it depends on [user?.id]
    // and the id won't change again). Setting it pre-signup guarantees
    // the flag is observable from the very first effect run.
    // Cookie-scoped to .telosintelligence.ai so the marketing-host signup
    // flag is visible when the user lands on app.telosintelligence.ai.
    markFreshSignup();
    try {
      const fresh = await signUp(name, email, password, {
        affiliateCode: affiliateCode.trim() || undefined,
        promoCode: promoCode.trim() || undefined,
      });
      // The affiliate code (if used) has now been recorded server-side
      // on this user — clear the stored landing-page capture so the next
      // person who signs up on this browser doesn't inherit it.
      try { localStorage.removeItem('telos:affiliate-ref'); } catch { /* ignore */ }
      // Branch based on plan:
      //  - Promo signup (SKIP123 etc) bypassed paywall, plan is already
      //    pro/elite -> go straight to /onboarding then dashboard + tour.
      //  - Free signup has no paid plan -> go to /dashboard so RequirePaid
      //    surfaces TrialExpired (the paywall). After they pay, the post-
      //    payment handler in DashboardRoute routes them to /onboarding.
      const plan = (fresh as { plan?: string } | null)?.plan || '';
      const isPaid = plan === 'pro' || plan === 'pro_yearly' || plan === 'elite' || plan === 'elite_yearly';
      navigate(isPaid ? '/onboarding' : '/', { replace: true });
    } catch (err: any) {
      // Signup failed — clear the fresh-signup flag we set above so a
      // returning-user sign-in on this browser doesn't inherit it.
      // Clear the flag we set above so a returning sign-in doesn't inherit it.
      try { localStorage.removeItem('telos-fresh-signup'); } catch { /* ignore */ }
      try { document.cookie = 'telos-fresh-signup=; Path=/; Max-Age=0; Domain=.telosintelligence.ai; SameSite=Lax'; } catch { /* ignore */ }
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  // Already-signed-in handling. The route used to be wrapped in PublicOnly
  // which silently redirected to /dashboard, so visiting /signup with an
  // existing session skipped onboarding entirely. Now we show an explicit
  // prompt — "you're signed in, sign out to create a new account" — so the
  // path to a fresh signup is unambiguous.
  if (user) {
    return (
      <AuthCard
        title="You're already signed in"
        subtitle={`Logged in as ${user.email}. Sign out to create a new account, or open your dashboard.`}
      >
        <div className="space-y-3">
          <Button
            variant="primary"
            className="w-full"
            onClick={async () => {
              await signOut();
              // After sign-out the form re-renders below — no extra navigation needed.
            }}
          >
            Sign out &amp; create new account
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              // Force the app subdomain so we don't land on the marketing
              // Landing page (which is what '/' resolves to on telosintelligence.ai).
              if (typeof window !== 'undefined' && (window.location.hostname === 'telosintelligence.ai' || window.location.hostname === 'www.telosintelligence.ai')) {
                window.location.href = 'https://app.telosintelligence.ai/';
              } else {
                navigate('/', { replace: true });
              }
            }}
          >
            Go to dashboard
          </Button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Create your account" subtitle="Start your 7-day free trial — cancel anytime.">
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="space-y-4"
      >
        <Input label="Full name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Trader" required autoComplete="name" autoFocus />
        <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" />
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            required
            autoComplete="new-password"
            minLength={8}
            hint="Minimum 8 characters."
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            tabIndex={-1}
            className={cn(
              'absolute right-2 top-[26px] inline-flex items-center justify-center size-9 rounded-md',
              'text-fg-subtle hover:text-fg transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
            )}
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setCodesOpen((x) => !x)}
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium',
              'text-fg-muted hover:text-fg transition-colors',
              'focus-visible:outline-none focus-visible:underline',
            )}
            aria-expanded={codesOpen}
          >
            I have a code
            <motion.span
              animate={{ rotate: codesOpen ? 180 : 0 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex"
            >
              <ChevronDown className="size-3.5" />
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {codesOpen && (
              <motion.div
                key="codes"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  <Input
                    label="Have a promo code? (optional)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="e.g. LAUNCH50"
                    autoComplete="off"
                    spellCheck={false}
                    className="tabular"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {error && (
          <div role="alert" className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <Button type="submit" size="lg" loading={loading} className="w-full">Create account</Button>
        {affiliateCode && (
          <div className="text-center text-2xs text-fg-subtle">
            Referred via code <span className="tabular font-medium">{affiliateCode}</span>
          </div>
        )}
      </motion.form>
      <div className="mt-6 text-center text-xs text-fg-muted">
        Already have an account? <Link to="/signin" className="text-accent font-semibold hover:underline">Sign in</Link>
      </div>
      <div className="mt-2 text-center text-2xs text-fg-subtle">
        Want to partner with us? <Link to="/affiliate-signup" className="text-accent font-medium hover:underline">Become an affiliate</Link>
      </div>
    </AuthCard>
  );
}
